import React, {
  useEffect,
  useState,
  useCallback,
} from 'react';
import { withFormik, Form } from 'formik';
import queryString from 'query-string';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import _ from 'lodash';

import { captureException } from '@playbookux/playbook-web-core';
import {
  Container,
  Loader,
  Message,
  TextError,
} from './styles';
import { checkToken, createRecruitToken } from './requests';

import schema from './schema';

import {
  clearQuestions,
  setProjectValues,
  setTestSession,
  signInWithTester,
  signOut,
  authenticateWithScreener,
} from '../../../core/actions';

import TestTypes from '../../../core/constants/testTypes';
import Devices from '../../../core/constants/device-types';
import ParticipantsSource from '../../../core/constants/participantsSource';
import { camelCase } from '../../../core/utils/camel-case';

import { withTranslation } from '../../../core/i18n';
import withIntercom from '../../../core/components/with-intercom';
import FormMobile from '../recruit-participants/form-mobile';
import FormComputer from '../recruit-participants/form-computer';

const GuestTester = (props) => {
  const {
    location,
    setFieldValue,
    setLocale,
    handleSubmit,
    status,
    values,
    onClearQuestions,
    onSetProjectValues,
    onSignOut,
    authentication,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [linkDeleted, setLinkDeleted] = useState(false);
  const { token, iid: userInterviewParticipantId } = queryString.parse(location.search);

  // regex to check if is running on mobile device (tablet or smartphone)
  const isMobile = () => (
    /Mobile|mobile|Android/.test(navigator.userAgent)
  );

  const handleCheckToken = useCallback(async () => {
    try {
      const response = await checkToken(token);

      const { project, segment: responseSegment } = response;

      _.forIn(project, (value, key) => {
        const projectKey = project[key];
        delete project[key];
        project[camelCase(key)] = projectKey;
      });

      // Update project with correct device and participantsLanguage
      // which are stored on the segment
      project.device = responseSegment.device;
      project.participantsLanguage = responseSegment.participants_language;
      project.operatingSystem = responseSegment.operating_system;

      // Set the initial values of the project in the redux.
      onSetProjectValues(project);

      // Set locale based on project configuration
      setLocale(project.participantsLanguage);
      setFieldValue('project', project);
      setFieldValue('segment', responseSegment);

      if (response.deleted_at) {
        setLinkDeleted(true);
      } else {
        setLinkDeleted(false);
      }

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setError(err);
      captureException('HANDLE_CHECK_TOKEN_FAILED', err);
    }
  }, [onSetProjectValues, setFieldValue, setLocale, token]);

  // Used as componentDidMount
  useEffect(() => {
    // this prevent guest user taking role as user logged
    // which means guest can update profile and make test from browser tests
    if (authentication?.token) {
      onSignOut();
    }

    // Used to clear all the Tester answered questions in the redux before the survey starts.
    onClearQuestions();

    handleCheckToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error && userInterviewParticipantId && status && status.error && status.error.code === 'record_not_unique') {
    return (
      <Container>
        <Message>User Interviews Participant already exists</Message>
      </Container>
    );
  }

  if (userInterviewParticipantId && status && status.error && status.error.message === 'user_interviews_not_found') {
    return (
      <Container>
        <Message>Your tester session in User Interviews does not exist. Please contact support</Message>
      </Container>
    );
  }

  if (userInterviewParticipantId && status && status.error) {
    // eslint-disable-next-line no-console
    console.error(`User interviews token validation error: ${error || status.error}`);

    return (
      <Container>
        <Message>You’ve already taken this study!</Message>
      </Container>
    );
  }

  if (error || (status && status.error)) {
    return (
      <Container>
        <Message>
          {error.message || status.error.message}
        </Message>
      </Container>
    );
  }

  if (isLoading || !values.project) {
    return (
      <Loader data-loader="guest-loader" />
    );
  }

  if (linkDeleted) {
    return (
      <Container>
        <Message>This study is no longer taking responses.</Message>
      </Container>
    );
  }

  const { participantsSource, company } = values.project;

  const isRYO = participantsSource === ParticipantsSource.COMPANY;
  const collectTesterName = !(isRYO && !company.collect_byo_testers_info);

  if (values && values.project && values.project.recorderType === 'chrome_extension' && isMobile()) {
    let deviceType = 'smartphone';
    if (window.innerWidth >= 540) {
      deviceType = 'tablet';
    }
    return (
      <Container center>
        <TextError>
          {`You're using the wrong device! Please take this study on a computer, not a ${deviceType}.`}
        </TextError>
      </Container>
    );
  }

  return (
    <Container>
      {values.project.device !== Devices.COMPUTER ? (
        <Form>
          <FormMobile
            onClick={handleSubmit}
            status={status}
            collectTesterName={collectTesterName}
          />
        </Form>
      ) : (
        <FormComputer
          onClick={handleSubmit}
          status={status}
          project={values.project}
          collectTesterName={collectTesterName}
        />
      )}
    </Container>
  );
};

GuestTester.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  status: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.shape({
    project: PropTypes.shape({
      recorderType: PropTypes.string,
      device: PropTypes.string,
      testType: PropTypes.string,
      participantsSource: PropTypes.string,
      company: PropTypes.shape({
        collect_byo_testers_info: PropTypes.bool,
      }),
    }),
  }).isRequired,
  setLocale: PropTypes.func.isRequired,
  onClearQuestions: PropTypes.func.isRequired,
  onSetProjectValues: PropTypes.func.isRequired,
  onSignOut: PropTypes.func.isRequired,
  authentication: PropTypes.shape().isRequired,
};

GuestTester.defaultProps = {
  status: false,
};

const mapStateToProps = ({ authentication }) => ({
  authentication,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    onSignInWithTester: signInWithTester,
    onClearQuestions: clearQuestions,
    onSetProjectValues: setProjectValues,
    onSetTestSession: setTestSession,
    onSignOut: signOut,
    onAuthenticateWithScreener: authenticateWithScreener,
  },
  dispatch,
);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withFormik({
    validationSchema: schema,
    mapPropsToValues: () => ({
      firstName: '',
      lastName: '',
    }),
    handleSubmit: async (values, { props, setStatus }) => {
      const {
        project: {
          device,
          participantsLanguage: language,
          recorderType,
          testType,
          projectConsentForms,
        },
        firstName,
        lastName,
        segment,
      } = values;

      const consentForms = _.map(projectConsentForms, ({ consent_form: cf }) => cf);

      setStatus({
        isLoading: true,
      });

      try {
        const hasWelcomePage = [TestTypes.CARD_SORT, TestTypes.TREE_TEST, TestTypes.SURVEY].includes(testType);
        const showCintId = hasWelcomePage;
        const showMrId = hasWelcomePage;
        const {
          token, cintid, tk: mrid, iid: userInterviewsParticipantId,
        } = queryString.parse(props.location.search);
        const cintIdValue = showCintId ? cintid : null;
        const mrIdValue = showMrId ? mrid : null;

        const {
          screener_session: screenersSession,
          test_session: testSession,
          demographics_form_required: demographicsFormRequired,
        } = await createRecruitToken(token, firstName, lastName, cintIdValue, mrIdValue, userInterviewsParticipantId);

        const {
          onSignInWithTester,
          onSetTestSession,
          onAuthenticateWithScreener,
          history,
        } = props;

        const hasConsentForms = !_.isEmpty(consentForms);
        const isUserInterviewsTester = !_.isEmpty(userInterviewsParticipantId);

        if (hasConsentForms && !isUserInterviewsTester) {
          if (screenersSession && demographicsFormRequired) {
            const {
              id,
              authentication_token: authenticationToken,
              tester_id: testerId,
            } = screenersSession;

            await onAuthenticateWithScreener(id, authenticationToken);

            const pathScreenerSession = `/screener-session?session-id=${id}`;
            const pathTokenAndGuest = `&token=${authenticationToken}&guest=true&lang=${language}`;
            const pathDemographicsSurvey = `&demographics-survey=${demographicsFormRequired}`;

            const redirectPath = `${pathScreenerSession}${pathTokenAndGuest}${pathDemographicsSurvey}`;

            if (cintIdValue && mrIdValue) {
              history.push(`/consent-form?testerId=${testerId}#${redirectPath}&cintid=${cintIdValue}}&tk=${mrIdValue}`);
              return;
            }
            history.push(`/consent-form?testerId=${testerId}#${redirectPath}`);
          } else if (testSession) {
            const {
              id,
              authentication_token: authenticationToken,
              tester_id: testerId,
            } = testSession;

            // Set the values of the current test session in the redux.
            onSetTestSession(testSession);

            await onSignInWithTester(id, authenticationToken);

            let redirectPath;
            if (testType !== TestTypes.MODERATED && demographicsFormRequired) {
              if (testType === TestTypes.UNMODERATED) {
                redirectPath = `/demographics-survey?session-id=${id}&testType=${testType}&token=${authenticationToken}`
                  + `&device=${device}&recorder=${recorderType}&lang=${language}`;
              } else {
                redirectPath = `/demographics-survey?session-id=${id}&testType=${testType}`;
              }
            } else if (hasWelcomePage) {
              redirectPath = `/welcome?session-id=${id}`;
            } else if (testType === 'moderated') {
              redirectPath = `/schedule?segment_id=${segment.id}`;
              history.push(`/consent-form?testerId=${testerId}#${redirectPath}`);
            } else {
              redirectPath = `/unmoderated-session?session-id=${id}&token=${authenticationToken}`
                + `&device=${device}&recorder=${recorderType}&lang=${language}`;
            }

            if (cintIdValue) {
              redirectPath = `${redirectPath}&cintid=${cintIdValue}`;
            }

            if (mrIdValue) {
              redirectPath = `${redirectPath}&tk=${mrIdValue}`;
            }

            history.push(`/consent-form?testerId=${testerId}#${redirectPath}`);
          }
          return;
        }

        // If the tester is from User Interviews, we need to skip the screeners and go directly to the test session
        // This will avoid the tester have an issue if the recruiting linking is created wrongly with demographic enabled
        if (screenersSession && demographicsFormRequired && !isUserInterviewsTester) {
          let redirectPath = `/screener-session?session-id=${screenersSession.id}`
            + `&token=${screenersSession.authentication_token}&guest=true&lang=${language}&demographics-survey=true`;

          if (demographicsFormRequired) {
            redirectPath += `&demographics-survey=${demographicsFormRequired}`;
          }

          if (cintIdValue) {
            redirectPath += `&cintid=${cintIdValue}`;
          }

          if (mrIdValue) {
            redirectPath += `&tk=${mrIdValue}`;
          }
          history.push(redirectPath);
        } else if (testSession) {
          const {
            authentication_token: authenticationToken,
          } = testSession;

          // Set the values of the current test session in the redux.
          onSetTestSession(testSession);

          await onSignInWithTester(testSession.id, authenticationToken);
          if (testType !== TestTypes.MODERATED && demographicsFormRequired && !isUserInterviewsTester) {
            history.push(`/demographics-survey?session-id=${testSession.id}&testType=${testType}`);
          } else if (hasWelcomePage) {
            history.push(`/welcome?session-id=${testSession.id}`);
          } else {
            history.push(
              `/unmoderated-session?session-id=${testSession.id}&token=${testSession.authentication_token}`
              + `&device=${device}&recorder=${recorderType}&lang=${language}`,
            );
          }
        }
      } catch (error) {
        setStatus({
          error,
        });
        captureException('HANDLE_SUBMIT_GUEST_TESTER_FAILED', error);
      }
    },
  }),
  withRouter,
  withIntercom,
  withTranslation,
)(GuestTester);
